// Share a generated PNG (as a data URL) using the best available mechanism.
//
// - Native (Capacitor iOS/Android): write the image to a temp file and open the
//   real OS share sheet (AirDrop, Messages, Save to Photos, Instagram, ...).
//   This replaces the unreliable long-press-to-save WebView gesture.
// - Web with Web Share API level 2: share the File directly via navigator.share.
// - Web fallback: trigger a normal download.
//
// Returns 'shared' | 'downloaded' | 'cancelled'.
export async function shareImage(dataUrl, filename = 'benderquest.png', title = 'BenderQuest') {
  if (!dataUrl) return 'cancelled';

  const isNative = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

  if (isNative) {
    try {
      // Dynamic import so these native-only modules never load on the plain web build.
      const [{ Filesystem, Directory }, { Share }] = await Promise.all([
        import('@capacitor/filesystem'),
        import('@capacitor/share'),
      ]);
      const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      const written = await Filesystem.writeFile({
        path: filename,
        data: base64,
        directory: Directory.Cache,
      });
      await Share.share({ title, files: [written.uri] });
      return 'shared';
    } catch (e) {
      // User dismissing the sheet throws on iOS — treat as a non-error cancel.
      if (isShareCancel(e)) return 'cancelled';
      console.error('Native share failed:', e);
      // Fall through to the web paths as a last resort.
    }
  }

  // Web Share API (level 2 — files). Must run inside a user gesture to succeed.
  try {
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], filename, { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title });
      return 'shared';
    }
  } catch (e) {
    if (isShareCancel(e)) return 'cancelled';
    // else fall through to download
  }

  // Final fallback: download the file.
  try {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    return 'downloaded';
  } catch (e) {
    console.error('Image download fallback failed:', e);
    return 'cancelled';
  }
}

function isShareCancel(e) {
  const msg = (e && (e.message || String(e))) || '';
  return e?.name === 'AbortError' || /cancel|abort|dismiss/i.test(msg);
}
